import { Stack, Typography, useTheme } from "@mui/material";
import { type FC, useMemo } from "react";
import {
	type ClientPropertyItemResponseDTO,
	ClientPropertyTypeEnum,
	ClientPropertyVehicleTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CommonListItem } from "web_myplann/Portfolio/Components/CommonListItem";
import { getPortfolioIcon } from "web_myplann/Portfolio/Helpers/getPropertyIcon";
import { propertyCarTitle } from "web_myplann/Portfolio/Helpers/utils";
import { ListItemMoneyValue } from "web_myplann/Shared/Components/ListItemMoneyValue";

type Props = {
	data: ClientPropertyItemResponseDTO;
	onClick: () => void;
};

export const PropertyListItemTitle: FC<Props> = ({ data, onClick }) => {
	const { t } = useResource();
	const theme = useTheme();

	const element = useMemo(() => {
		let title =
			data.property !== ClientPropertyTypeEnum.AutoMoto
				? data.name
				: data.propertyType !== ClientPropertyVehicleTypeEnum.None
					? t(
							Resources.PortfolioDetail.Property.CarPropertyTypeValue[
								data.propertyType as ClientPropertyVehicleTypeEnum
							],
						)
					: "";

		if (title === undefined || title === null || title === "") {
			title =
				data.property != null
					? t(Resources.PortfolioDetail.Property.PropertyType[data.property])
					: "";
		}

		if (data.property === ClientPropertyTypeEnum.AutoMoto) {
			return (
				<Stack direction="column">
					<Typography
						variant="deprecatedH3"
						color={theme.palette.deprecated.grey[700]}
					>
						{title}
					</Typography>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[500]}
						sx={{
							display: "-webkit-box",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 2,
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{propertyCarTitle(
							data.carBrand,
							data.carModel,
							data.carLicensePlate,
						)}
					</Typography>
					<ListItemMoneyValue
						value={data.value}
						currencySign={t(Resources.Common.CurrencySign)}
					/>
				</Stack>
			);
		}

		if (
			data.property === ClientPropertyTypeEnum.Flat ||
			data.property === ClientPropertyTypeEnum.House ||
			data.property === ClientPropertyTypeEnum.Cottage ||
			data.property === ClientPropertyTypeEnum.Land
		) {
			let location = "";

			const parts = [
				data.location,
				data.locationMunicipality,
				data.locationZipCode,
			].filter(Boolean);

			if (parts.length > 0) {
				location = parts.join(", ");
			}

			return (
				<Stack direction="column">
					<Typography
						variant="deprecatedH3"
						color={theme.palette.deprecated.grey[700]}
					>
						{title}
					</Typography>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[500]}
					>
						{location}
					</Typography>
					<ListItemMoneyValue
						value={data.marketValue}
						currencySign={t(Resources.Common.CurrencySign)}
					/>
				</Stack>
			);
		}

		if (data.property === ClientPropertyTypeEnum.Art) {
			return (
				<Stack direction="column">
					<Typography
						variant="deprecatedH3"
						color={theme.palette.deprecated.grey[700]}
					>
						{title}
					</Typography>
					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[500]}
					>
						{data.propertyType}
					</Typography>
					<ListItemMoneyValue
						value={data.value}
						currencySign={t(Resources.Common.CurrencySign)}
					/>
				</Stack>
			);
		}

		return (
			<Stack direction="column">
				<Typography
					variant="deprecatedH3"
					color={theme.palette.deprecated.grey[700]}
				>
					Error
				</Typography>
			</Stack>
		);
	}, [data, theme]);

	return (
		<CommonListItem
			icon={getPortfolioIcon(theme, data.property)}
			iconText={data.iconText ?? undefined}
			element={element}
			onClick={onClick}
		/>
	);
};
